import type { TimelineElement } from '../../../context/TimelineContext'
import type { WorkspacePulseSnapshot } from '../../aiAgentBridge'

export const buildLocalWorkspacePulseSnapshot = (
  workspaceId: string,
  elements: TimelineElement[],
): WorkspacePulseSnapshot => {
  const activeElements = elements.filter((element) => element.workspaceId === workspaceId && element.board !== 'BIN' && element.board !== 'ARCHIVE')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const overdue = activeElements.filter((element) => {
    if (element.boardStatus === 'COMPLETE' || element.workflowStatus === 'COMPLETE' || !element.endDate) return false
    const end = new Date(element.endDate)
    return Number.isFinite(end.getTime()) && end < today
  })
  const invalidDependencies = activeElements.filter((element) => (element.invalidDependencyNodeIds ?? []).length > 0)
  const budgeted = activeElements.filter((element) => Number(element.budgetLimit ?? 0) > 0)
  const totalBudgetLimit = budgeted.reduce((sum, element) => sum + Number(element.budgetLimit ?? 0), 0)
  const totalActualSpend = activeElements.reduce((sum, element) => sum + Number(element.estimatedCost ?? 0), 0)
  const utilization = totalBudgetLimit > 0 ? totalActualSpend / totalBudgetLimit : 0
  const overBudgetCount = budgeted.filter((element) => Number(element.estimatedCost ?? 0) > Number(element.budgetLimit ?? 0)).length
  const risks = [
    ...overdue.slice(0, 3).map((element) => ({ id: `local-overdue-${element.id}`, targetId: element.id, summary: `${element.title} is past its end date.`, source: 'LOCAL_SCHEDULE' })),
    ...invalidDependencies.slice(0, 2).map((element) => ({ id: `local-dependency-${element.id}`, targetId: element.id, summary: `${element.title} has unresolved dependencies.`, source: 'LOCAL_DEPENDENCY' })),
  ]
  const riskScore = Math.min(100, overdue.length * 12 + invalidDependencies.length * 14 + overBudgetCount * 10 + (utilization >= 1 ? 24 : utilization >= 0.8 ? 12 : 0))
  return {
    workspaceId,
    healthState: riskScore >= 70 ? 'CRITICAL' : riskScore >= 35 ? 'WATCH' : 'HEALTHY',
    riskScore,
    openRiskCount: risks.length,
    failedAutomationCount: 0,
    stockShortageCount: invalidDependencies.length,
    walletCredits: 0,
    budget: {
      totalBudgetLimit,
      totalActualSpend,
      utilization,
      overBudgetCount,
    },
    activity: {
      last24hCount: activeElements.length,
      latest: activeElements.slice(0, 5).map((element) => ({
        id: `local-activity-${element.id}`,
        action: element.boardStatus ?? element.workflowStatus ?? 'DRAFT',
        summary: element.title,
        targetId: element.id,
      })),
    },
    topRisks: risks.length ? risks : [{ id: 'local-clear', targetId: workspaceId, summary: 'No local schedule, dependency, or budget pressure detected.', source: 'LOCAL_CLEAR' }],
  }
}
